import { Helmet } from '@dr.pogodin/react-helmet';

interface SEOHelmetProps {
  title: string;
  description: string;
  canonical?: string;
  image?: string;
  imageAlt?: string;
  twitterImage?: string;
  type?: 'website' | 'article' | 'product';
  keywords?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  /**
   * Emit robots noindex,follow. For faceted pages whose every filter combination
   * would otherwise be a separate crawlable URL. Links are still followed, so
   * crawl equity flows through to the cards themselves.
   */
  noindex?: boolean;
}

/**
 * Normalize a canonical URL.
 *
 * Only the origin and path are lowercased. A query string must keep its case:
 * ?name=Ancestral Recall and ?name=ancestral recall are different values, and
 * flattening them would point the canonical at a URL that returns something else.
 */
const normalizeCanonical = (raw: string): string => {
  const queryIndex = raw.indexOf('?');
  if (queryIndex === -1) return raw.toLowerCase().replace(/\/$/, '');
  const base = raw.slice(0, queryIndex).toLowerCase().replace(/\/$/, '');
  return `${base}${raw.slice(queryIndex)}`;
};

/**
 * SEOHelmet - Reusable component for managing meta tags
 *
 * Usage:
 * <SEOHelmet
 *   title="TradeMagic - Trade MTG Cards"
 *   description="The easiest way to trade Magic cards"
 *   canonical="https://trademagic.gg/"
 *   image="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/logos/banner_social_share.png"
 *   twitterImage="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/logos/banner_social_share_twitter.png"
 *   type="website"
 * />
 */
export const SEOHelmet: React.FC<SEOHelmetProps> = ({
  title,
  description,
  canonical,
  image = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/logos/banner_social_share.png',
  imageAlt = title,
  twitterImage = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/logos/banner_social_share_twitter.png',
  type = 'website',
  keywords,
  author = 'TradeMagic',
  publishedTime,
  modifiedTime,
  twitterCard = 'summary_large_image',
  noindex = false,
}) => {
  // Ensure canonical URL follows standards (lowercase, no trailing slash).
  // Fall back to the current page URL (self-canonical) when no canonical prop is
  // provided — emitting the homepage as canonical from a non-homepage route
  // signals soft-404 to Google.
  const fallbackCanonical =
    typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname}`
      : 'https://trademagic.gg/';

  const canonicalUrl = normalizeCanonical(canonical || fallbackCanonical);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {author && <meta name="author" content={author} />}

      {/* Canonical URL - must be explicitly set to override any existing canonical */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Always emitted, because public/index.html ships a static
          <meta name="robots" content="index, follow">. Rendering this tag only
          in the noindex case would leave that static one in place and the page
          would stay indexable. */}
      <meta name="robots" content={noindex ? 'noindex, follow' : 'index, follow'} />

      {/* Open Graph / Facebook / Discord / LinkedIn */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:site_name" content="TradeMagic" />

      {/* Article-specific Open Graph tags */}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={twitterImage} />
      <meta name="twitter:image:alt" content={imageAlt} />
      <meta name="twitter:site" content="@trademagic_gg" />
      <meta name="twitter:creator" content="@trademagic_gg" />
    </Helmet>
  );
};
